/**
 * 新首页 - 食品数字标签生成系统
 * 基于 Intertek 风格设计
 */

import React, { useState, useEffect } from 'react';
import {
  Search,
  Menu,
  X,
  ChevronRight,
  Phone,
  ArrowRight,
} from 'lucide-react';

// --- 图标组件 ---
const WeChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M8.5,13.5c-0.3,0-0.5-0.2-0.5-0.5c0-0.3,0.2-0.5,0.5-0.5s0.5,0.2,0.5,0.5C9,13.3,8.8,13.5,8.5,13.5z M10.9,13.5 c-0.3,0-0.5-0.2-0.5-0.5c0-0.3,0.2-0.5,0.5-0.5s0.5,0.2,0.5,0.5C11.4,13.3,11.2,13.5,10.9,13.5z M17,10c0-2.5-2.5-4.5-5.5-4.5 S6,7.5,6,10c0,1.3,0.7,2.4,1.8,3.2c0.2,0.1,0.2,0.4,0.1,0.6l-0.4,1.4c-0.1,0.2,0.1,0.5,0.4,0.4l1.6-0.7c0.2-0.1,0.4-0.1,0.6,0 c0.5,0.1,0.9,0.2,1.4,0.2c3,0,5.5-2,5.5-4.5z M22,15.5c0-2.1-2.1-3.8-4.8-3.8c-0.1,0-0.3,0-0.4,0c0.1,0.4,0.2,0.8,0.2,1.2 c0,3.2-3.1,5.8-7,5.8c-0.4,0-0.8,0-1.1-0.1c0.8,1.4,2.5,2.4,4.5,2.4c0.6,0,1.1-0.1,1.7-0.2c0.2-0.1,0.5,0,0.7,0.1l1.6,0.9 c0.3,0.1,0.6-0.1,0.5-0.4l-0.5-1.5c0-0.2,0-0.5,0.1-0.6C21,18.4,22,17.1,22,15.5z"/>
  </svg>
);

// --- 1. 顶部导航条 ---
const TopBar = () => (
  <div className="bg-white text-[#666] text-[12px] py-2 hidden md:block font-sans border-b border-gray-100 relative z-50">
    <div className="container mx-auto px-4 flex justify-end items-center space-x-6 font-medium">
      <a href="#" className="hover:text-[#00A3E0] transition-colors">帮助中心</a>
      <a href="#" className="hover:text-[#00A3E0] transition-colors">关于我们</a>
      <a href="#" className="hover:text-[#00A3E0] transition-colors">联系我们</a>
      <a href="#" className="hover:text-[#00A3E0] transition-colors">官网</a>
    </div>
  </div>
);

// --- 2. 透明头部导航 ---
const TransparentHeader = ({ isScrolled, onLoginClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-40 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 shadow-md py-2' : 'bg-transparent py-4 md:py-6'
      }`}
      style={{ top: isScrolled ? 0 : '36px' }}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <a
            href="#"
            className="flex items-center"
          >
            <div className="flex flex-col">
              {/* Logo - 黄色品牌色 */}
              <span className="text-2xl md:text-3xl font-black tracking-tighter text-[#FFD100] leading-none" style={{ fontFamily: 'Arial, sans-serif' }}>
                数字标签系统
              </span>
              <span className={`text-[10px] font-bold uppercase tracking-widest mt-1 transition-colors ${
                isScrolled ? 'text-gray-500' : 'text-white/90'
              }`}>
                内测版
              </span>
            </div>
          </a>

          {/* 桌面导航 */}
          <div className="hidden lg:flex items-center justify-end flex-1 ml-12">
            <nav className={`flex space-x-10 font-bold text-[15px] mr-8 transition-colors ${
              isScrolled ? 'text-gray-700' : 'text-white'
            }`}>
              <a href="#features" className="hover:text-[#FFD100] transition-colors">功能介绍</a>
              <a href="#industries" className="hover:text-[#FFD100] transition-colors">行业方案</a>
              <a href="#services" className="hover:text-[#FFD100] transition-colors">服务流程</a>
              <a href="#news" className="hover:text-[#FFD100] transition-colors">资讯动态</a>
            </nav>

            {/* 搜索框 */}
            <div className="relative group mr-4">
              <input
                type="text"
                placeholder="搜索..."
                className="pl-5 pr-10 py-2 rounded-full border border-transparent bg-white focus:outline-none focus:ring-2 focus:ring-[#FFD100] w-48 text-sm transition-all text-gray-800 shadow-lg"
              />
              <Search className="absolute right-3 top-2.5 text-gray-400 group-hover:text-[#00A3E0] transition-colors" size={18} />
            </div>

            {/* 登录按钮 */}
            <button
              onClick={onLoginClick}
              className="px-6 py-2 rounded-full bg-[#FFD100] text-black font-bold hover:bg-white hover:text-[#00A3E0] transition-colors shadow-md"
            >
              登录/注册
            </button>
          </div>

          {/* 移动端菜单按钮 */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={isScrolled ? "text-gray-800" : "text-white"}
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* 移动端菜单 */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white shadow-lg py-4">
            <nav className="flex flex-col space-y-4 px-4">
              <a href="#features" className="text-gray-700 font-medium">功能介绍</a>
              <a href="#industries" className="text-gray-700 font-medium">行业方案</a>
              <a href="#services" className="text-gray-700 font-medium">服务流程</a>
              <a href="#news" className="text-gray-700 font-medium">资讯动态</a>
              <button
                onClick={onLoginClick}
                className="px-6 py-2 rounded-full bg-[#FFD100] text-black font-bold"
              >
                登录/注册
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// --- 3. Hero 大图横幅 ---
const Hero = ({ onGetStarted }) => (
  <section className="relative h-[600px] md:h-[700px] w-full bg-gray-900 overflow-hidden group">
    {/* 背景视频 */}
    <video
      autoPlay
      loop
      muted
      playsInline
      className="absolute inset-0 w-full h-full object-cover opacity-90"
    >
      <source src="/hero-video.mp4" type="video/mp4" />
    </video>
    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/30"></div>

    <div className="absolute inset-0 flex flex-col justify-center items-center text-white text-center pt-20 px-4">
      <h1 className="text-5xl md:text-7xl font-extrabold mb-6 drop-shadow-lg tracking-tight">
        <span className="text-[#FFD100]">让每一份食品</span><br />
        <span className="text-white">都有数字身份证</span>
      </h1>
      <p className="text-lg md:text-xl max-w-4xl mb-10 font-medium drop-shadow-md text-white/90">
        3分钟生成专业标签 · 100%符合国家标准 · AI智能填充
      </p>
      <button
        onClick={onGetStarted}
        className="bg-[#FFD100] text-black font-bold py-3.5 px-10 rounded-full hover:bg-white hover:text-[#00A3E0] transition-all uppercase tracking-wide text-sm shadow-xl transform hover:-translate-y-1"
      >
        免费开始使用
      </button>
    </div>
  </section>
);

// --- 4. 联系条 ---
const ActionStrip = () => (
  <div className="bg-white py-8 border-b border-gray-100">
    <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center">
      <span className="text-xl font-bold text-[#111] mb-6 md:mb-0">需要帮助或有疑问？</span>
      <div className="flex items-center gap-4">
        <button className="flex items-center px-8 py-2.5 rounded-full border border-gray-300 text-gray-800 font-bold hover:border-[#00A3E0] hover:text-[#00A3E0] transition-colors bg-white text-lg">
           <Phone size={18} className="mr-3 text-[#00A3E0]"/>
           029-XXXXXXXX
        </button>
        <button className="px-10 py-3 rounded-full bg-[#00A3E0] text-white font-bold hover:bg-[#008cc2] transition-colors shadow-md text-lg">
          咨询留言
        </button>
      </div>
    </div>
  </div>
);

// --- 5. 特色卡片 ---
const FeatureCards = ({ onCreateClick }) => {
  const cards = [
    {
      title: "智能标签生成",
      desc: "填写产品信息，系统自动生成符合国标的数字标签，简单高效",
      img: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&q=80&w=800",
    },
    {
      title: "100%合规保障",
      desc: "严格遵循GB 28050等国家标准，内置合规检测引擎，确保标签合规",
      img: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=800",
    },
    {
      title: "二维码数字标签",
      desc: "生成专属二维码标签，消费者扫码即可查看完整产品信息，实现食品溯源与透明化",
      img: "https://images.unsplash.com/photo-1576086213369-97a306d36557?auto=format&fit=crop&q=80&w=800",
    }
  ];

  return (
    <section id="features" className="bg-[#F9F9F9] py-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <div
              key={index}
              onClick={index === 0 ? onCreateClick : undefined}
              className="bg-white group cursor-pointer shadow-sm hover:shadow-2xl transition-all duration-300 flex flex-col h-full relative top-0 hover:-top-2 rounded-sm overflow-hidden"
            >
              <div className="h-48 overflow-hidden relative">
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10"></div>
                <img src={card.img} alt={card.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" />
              </div>
              <div className="p-8 flex flex-col flex-grow relative">
                <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-[#00A3E0] transition-colors">{card.title}</h3>
                <p className="text-sm text-gray-500 mb-12 leading-relaxed">
                  {card.desc}
                </p>
                <div className="absolute bottom-8 left-8">
                   <div className="w-10 h-10 rounded-full bg-[#00A3E0] text-white flex items-center justify-center transition-transform hover:scale-110 hover:bg-[#008CC2]">
                     <ArrowRight size={20} />
                   </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 更多服务标签 */}
        <div className="mt-12 flex flex-col items-center">
          <div className="flex flex-wrap justify-center items-center gap-4 text-sm">
            <span className="text-gray-500 font-bold mr-2 text-lg">更多推荐服务:</span>
            {["营养成分计算", "批量导入", "模板库", "API接口"].map((text, i) => (
              <a key={i} href="#" className="px-6 py-2.5 rounded-full border border-gray-200 bg-white text-gray-600 font-bold hover:border-[#00A3E0] hover:text-[#00A3E0] transition-colors shadow-sm">
                {text}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// --- 6. 行业方案网格 ---
const Industries = () => {
  const industries = [
    { name: "乳制品", img: "https://images.unsplash.com/photo-1550583724-b2692b85b150?auto=format&fit=crop&q=80&w=400" },
    { name: "饮料", img: "https://images.unsplash.com/photo-1534353473418-4cfa6c56fd38?auto=format&fit=crop&q=80&w=400" },
    { name: "休闲零食", img: "https://images.unsplash.com/photo-1621939514649-280e2ee25f60?auto=format&fit=crop&q=80&w=400" },
    { name: "调味品", img: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&q=80&w=400" },
    { name: "烘焙食品", img: "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&q=80&w=400" },
    { name: "肉类制品", img: "https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?auto=format&fit=crop&q=80&w=400" },
    { name: "保健食品", img: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&q=80&w=400" },
    { name: "方便食品", img: "https://images.unsplash.com/photo-1612929633738-8fe44f7ec841?auto=format&fit=crop&q=80&w=400" },
  ];

  return (
    <section id="industries" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-extrabold text-[#333] mb-12 border-l-8 border-[#FFD100] pl-4">行业解决方案</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {industries.map((item, index) => (
            <div key={index} className="group relative h-48 overflow-hidden cursor-pointer shadow-md rounded-sm">
              <img
                src={item.img}
                alt={item.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-90 group-hover:opacity-100 transition-opacity"></div>

              <div className="absolute bottom-0 left-0 w-full p-6">
                <div className="w-8 h-1 bg-[#FFD100] mb-3 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                <h3 className="text-white font-bold text-xl tracking-wide group-hover:translate-x-1 transition-transform">{item.name}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- 7. ATIC 服务介绍 ---
const ServiceSection = () => {
  const services = [
    {
      title: "智能生成",
      icon: "/icons/service-generate.png",
      bgColor: "bg-[#FFD100]",
      desc: "填写产品信息，系统自动生成符合国标的专业数字标签，简单高效省时省力"
    },
    {
      title: "合规检测",
      icon: "/icons/service-compliance.png",
      bgColor: "bg-[#4D4D4D]",
      desc: "内置GB 28050等国家标准检测引擎，实时校验标签合规性，避免违规风险"
    },
    {
      title: "标签管理",
      icon: "/icons/service-manage.png",
      bgColor: "bg-[#00A3E0]",
      desc: "统一管理企业所有产品标签，支持分类、搜索、版本控制，高效协作"
    },
    {
      title: "二维码发布",
      icon: "/icons/service-qrcode.png",
      bgColor: "bg-white",
      borderColor: "border-2 border-gray-300",
      desc: "一键生成专属二维码，消费者扫码即可查看完整产品信息，提升品牌信任度"
    },
  ];

  return (
    <section id="services" className="py-28 bg-[#F4F4F4]">
      <div className="container mx-auto px-4">
        <div className="text-center mb-20">
          <h2 className="text-4xl font-extrabold text-[#333] mb-8">我们能为您做什么</h2>
          <p className="text-gray-500 text-lg max-w-3xl mx-auto leading-relaxed">
            从标签生成到合规检测，全流程数字化解决方案，以创新和定制的解决方案<br className="hidden md:block"/>助力食品企业快速完成产品标签制作与管理。
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 max-w-6xl mx-auto">
          {services.map((item, idx) => (
            <div key={idx} className="flex flex-col items-center text-center group cursor-pointer">
              {/* 大圆形图标 */}
              <div className={`w-40 h-40 rounded-full ${item.bgColor} ${item.borderColor || ''} flex items-center justify-center mb-10 shadow-lg group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-3 group-hover:scale-105`}>
                <img
                  src={item.icon}
                  alt={item.title}
                  className="w-20 h-20 object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-[#333] mb-4">{item.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed max-w-[260px] px-2">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- 8. 新闻资讯 ---
const NewsSection = () => {
  const news = [
    { title: "什么是食品「二维码标签」", date: "2025-06-15" },
    { title: "2025全球食品科技创新大会即将召开", date: "2025-03-20" },
    { title: "食品企业如何实现数字标签升级", date: "2025-10-10" }
  ];

  const regulations = [
    { title: "GB 28050-2024 预包装食品营养标签通则更新解读", date: "2025-05-10" },
    { title: "食品安全国家标准 预包装食品标签通则征求意见", date: "2025-05-01" },
    { title: "市场监管总局发布食品标签监督检查要点", date: "2025-04-20" }
  ];

  const CardRow = ({ title, items }) => (
    <div className="mb-20 last:mb-0">
      <div className="flex justify-between items-center mb-10 border-b border-gray-200 pb-4">
        <h3 className="text-3xl font-extrabold text-[#333]">{title}</h3>
        <button className="text-[#00A3E0] border border-[#00A3E0] px-6 py-2 rounded-full text-sm font-bold hover:bg-[#00A3E0] hover:text-white transition-colors uppercase">
          查看更多
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {items.map((item, idx) => (
          <div key={idx} className="bg-[#F9F9F9] p-8 min-h-[180px] flex flex-col justify-between relative group cursor-pointer hover:bg-white hover:shadow-xl transition-all border border-transparent hover:border-gray-100 rounded-sm">
            <h4 className="font-bold text-gray-800 text-lg leading-snug line-clamp-3 pr-4 group-hover:text-[#00A3E0] transition-colors">
              {item.title}
            </h4>
            <div className="flex justify-between items-end mt-6">
              <span className="text-sm text-gray-400 font-mono font-bold">{item.date}</span>
              <div className="w-10 h-10 rounded-full bg-[#E0E0E0] text-white flex items-center justify-center group-hover:bg-[#00A3E0] transition-colors">
                 <ArrowRight size={18} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section id="news" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <CardRow title="新闻和活动" items={news} />
        <CardRow title="法规标准更新" items={regulations} />
      </div>
    </section>
  );
};

// --- 9. 悬浮咨询按钮 ---
const StickyTab = () => (
  <div className="fixed right-0 top-1/2 transform -translate-y-1/2 z-50 hidden lg:flex">
    <div className="bg-[#FFD100] text-black font-bold py-6 px-2 rounded-l-md shadow-lg cursor-pointer hover:bg-[#ffdb4d] transition-colors flex flex-col items-center gap-3" style={{ writingMode: 'vertical-rl' }}>
       <span className="text-sm leading-4 tracking-widest">在线咨询</span>
       <div className="bg-white/30 rounded-full p-1.5 animate-pulse">
         <ChevronRight size={14} className="text-black" />
       </div>
    </div>
  </div>
);

// --- 10. 页脚 ---
const Footer = ({ onLoginClick }) => (
  <footer className="w-full">
    {/* 加入我们 */}
    <div className="bg-[#F8F9FA] py-12 md:py-16">
      <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-start md:items-center">
        <div className="mb-8 md:mb-0">
          <h3 className="text-3xl font-bold text-[#111] mb-2 tracking-wide">立即开始使用</h3>
          <p className="text-sm text-gray-500 font-bold uppercase tracking-widest mb-6">START NOW / 体验数字标签生成</p>
          <button
            onClick={onLoginClick}
            className="bg-[#00A3E0] text-white px-10 py-3 rounded-full font-bold text-base hover:bg-[#008cc2] transition-colors shadow-lg"
          >
            免费注册
          </button>
        </div>
      </div>
    </div>

    {/* 底部黑色区域 */}
    <div className="bg-black text-white py-6 md:py-8 border-t-[5px] border-[#FFD100]">
      <div className="container mx-auto px-4 flex flex-col lg:flex-row justify-between items-center gap-6">
         <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10 w-full lg:w-auto justify-center lg:justify-start">
           {/* Logo */}
           <div className="w-12 h-12 rounded-full border-2 border-[#FFD100] flex items-center justify-center shrink-0">
             <span className="text-[#FFD100] font-bold text-lg">标签</span>
           </div>

           {/* 链接 */}
           <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-300 font-medium">
             <a href="#" className="hover:text-white transition-colors">关于我们</a>
             <a href="#" className="hover:text-white transition-colors">联系我们</a>
             <a href="#" className="hover:text-white transition-colors">帮助中心</a>
             <a href="#" className="hover:text-white transition-colors">服务条款</a>
             <a href="#" className="hover:text-white transition-colors">隐私政策</a>
           </div>
         </div>

         <div className="text-center lg:text-right">
           <div className="text-xl font-black italic tracking-tighter mb-1">
             <span className="text-[#FFD100]">数字标签.</span> <span className="text-white">智能生成.</span>
           </div>
           <div className="text-[10px] text-gray-500 font-sans">
             <span className="block md:inline mr-2">数字标签系统内测版</span>

             <span className="ml-2">Copyright © 2025</span>
           </div>
         </div>
      </div>
    </div>
  </footer>
);

// --- 主组件 ---
const HomeNew = ({ onLoginClick, onGetStarted }) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen font-sans bg-white text-gray-900 selection:bg-[#FFD100] selection:text-black">
      <TopBar />

      <div className="relative">
        <TransparentHeader isScrolled={isScrolled} onLoginClick={onLoginClick} />
        <Hero onGetStarted={onGetStarted} />
      </div>

      <StickyTab />

      <main>
        <ActionStrip />
        <FeatureCards onCreateClick={onGetStarted} />
        <Industries />
        <ServiceSection />
        <NewsSection />
      </main>

      <Footer onLoginClick={onLoginClick} />
    </div>
  );
};

export default HomeNew;
